
import { SelectableItem, SelectableListState, SelectableStore, SelectableStoreDispatchers, SelectionDict, UniqKey } from '@/models/generics'
import { computed, ComputedRef, defineComponent, onMounted, PropType, reactive, ref, Ref, UnwrapNestedRefs, watch } from 'vue'
import { Store, useStore } from 'vuex'
import { CtTask, TaskType } from '@/models/interfaces/content-type'
import { $get, debounce } from '@/utils'
import { QVirtualScroll, QVirtualScrollProps, Screen } from 'quasar'
import _ from 'lodash'

export interface SelectableListEventHooksDict {
  add(id): any,
  select(id): any,
  remove(id): any,
  complete(): any
  next(search: string | boolean): any
}

export interface SelectableListEventsStoreDict {
  add: string,
  select: string,
  remove: string,
  complete: string,
  next: string
}

const STORE_EVENTS_DICT: Record<string, keyof SelectableStoreDispatchers> = {
  add: 'Add',
  // config: 'Config',
  remove: 'Remove',
  active: 'Active',
  activeItem: 'ActiveItem',
  select: 'Select',
  complete: 'Complete',
  next: 'Next'
}

// TODO: implement dragSelect as an optional driver for selection
export default defineComponent({
  name: 'SelectableList',
  props: {
    id: {
      type: String,
      default: Date.now().toString(16)
    },
    selectionId: {
      type: String,
      default: 'id'
    },
    parent: {
      type: String,
      default: ''
    },
    horizontal: {
      type: Boolean
    },
    cards: {
      type: Boolean
    },
    nextOnSelect: {
      type: Boolean
    },
    nextItemOnSelect: {
      type: Boolean,
      default: true
    },
    nextOnComplete: {
      type: Boolean,
      default: true
    },
    completeDelay: {
      type: Number
    },
    sort: {
      type: String,
    },
    items: {
      type: [Array, Object] as PropType<Array<SelectableItem | UniqKey> | Ref<Array<SelectableItem | UniqKey>>>
      // required: true
    },
    itemMeta: {
      type: Object as PropType<Record<string, any>>
    },
    // selection: {
    //   type: Object as PropType<SelectionDict<string>>
    // },
    hooks: {
      type: Object as PropType<SelectableListEventHooksDict>
    },
    store: {
      type: [Object, String] as PropType<UnwrapNestedRefs<SelectableStore>> | PropType<string>
      // type: String as PropType<string>
    },
    maxWidth: {
      type: [String, Boolean],
      default: '100%'
    },
    itemScrollSize: {
      type: [String, Number],
      default: 135
    },
    autoOverflow: {
      type: Boolean,
      default: false
    },
    autoscroll: {
      type: [Boolean, String] as PropType<boolean | 'active' | 'select' | 'complete' | 'activeSelect' | 'activeComplete' | 'selectComplete' | 'activeComplete'>,
      default: false
    },
    overflowOffset: {
      type: Number,
      default: 0
    },
    overflow: {
      type: Number,
      default: undefined
    },
    scrollAlign: {
      type: String as PropType<Parameters<QVirtualScroll['scrollTo']>[1]>,
      default: 'center'
    },
    // storeEventsDict: {
    //   type: Object as PropType<SelectableListEventsStoreDict>,
    //   default: () => STORE_EVENTS_DICT
    // }
  },
  emits: ['update:selection', 'select', 'remove', 'active', 'add', 'complete'],
  setup (props, { emit, slots }) {
    const $store = useStore()
    const list = ref()
    const listItemDOMElements = ref<Array<HTMLElement>>([])
    const listItemDOMElementSizes = ref<Array<number>>([])

    const cache = ref<Array<any>>([])
    const updateCache = (v) => _.merge(cache.value, v)

    const scrollable_area = ref<HTMLElement>()
    const computedOverflowHeight = computed(() => {
      return Screen.height - (scrollable_area.value?.getBoundingClientRect().top || 0) - props.overflowOffset
    })

    const internalStore = (props.store as UnwrapNestedRefs<SelectableStore>)?.getters || typeof props.store === 'string' && $store.hasModule(props.store)
      ? undefined
      : reactive<{ items?: Array<SelectableItem>, active?: string | Array<string>, activeItem?: string, selection: Record<UniqKey, SelectionDict> }>({
        items: undefined,
        active: undefined,
        activeItem: undefined,
        selection: {}
      })

    function getInternalItemIndex (id) {
      if (!internalStore?.items) return -1
      return internalStore.items.findIndex(item => _.get(item, props.selectionId) === id)
    }
    function getInternal (prop: string) {
      return internalStore?.[prop?.toLowerCase()]
    }
    function setInternal (action: keyof SelectableStoreDispatchers, ...args) {
      if (!internalStore) return
      switch (action) {
        case STORE_EVENTS_DICT.active:
          internalStore.active = args[0]
          break
        case STORE_EVENTS_DICT.activeItem:
          internalStore.activeItem = args[0]
          break
        case STORE_EVENTS_DICT.add:
          if (!internalStore.items) internalStore.items = []
          internalStore.items.push(args[0])
          break
        case STORE_EVENTS_DICT.remove:
          if (!internalStore.items) internalStore.items = []
          internalStore.items.splice(getInternalItemIndex(args[0]), 1)
          break
        case STORE_EVENTS_DICT.complete:
          if (!internalStore.items) break
          internalStore.items.splice(getInternalItemIndex(args[0]), 1)
          break
        case STORE_EVENTS_DICT.next:
          if (!internalStore.items) break
          internalStore.active = _.get(
            internalStore.items[getInternalItemIndex(internalStore.active) + 1],
            props.selectionId
          )
          break
        case STORE_EVENTS_DICT.select:
          internalStore.selection[args[0]] = { ...(internalStore.selection[args[0]] || {}), [args[1]]: args[2] === true }
          break
        default:
          return
      }
    }

    const store = {
      get: prop => {
        return (props.store as UnwrapNestedRefs<SelectableStore>)?.getters
          ? (props.store as UnwrapNestedRefs<SelectableStore>).getters[prop]
          : typeof props.store === 'string' && $store.hasModule(props.store)
            ? $store.getters[`${props.store}/${prop}`]
            : getInternal(prop)
      },
      set: async (action: keyof SelectableStoreDispatchers, ...args: Array<any>) => {
        return await ((props.store as UnwrapNestedRefs<SelectableStore>)?.dispatch
          ? (props.store as UnwrapNestedRefs<SelectableStore>).dispatch(action, ...args)
          : typeof props.store === 'string' && $store.hasModule(props.store)
            ? $store.dispatch(`${props.store}/${action}`, ...args)
            : setInternal(action, ...args))
      }
    }

    const state = reactive<SelectableListState>({
      Items: computed(() => (props.items as Ref<Array<SelectableItem>>)?.value || props.items || store.get('Items')),
      // Selection: computed(() => store.get('Selection') || {}),
      Selection: computed(() => {
        // console.log((store.get('Selection') || {}), props.parent, state.Active.value)
        return (store.get('Selection') || {})?.[props.parent || state.Active.value]
      }),
      Active: computed(() => store.get('Active') || []),
      ActiveItem: computed(() => store.get('ActiveItem')),
      ActiveTask: computed(() => store.get('ActiveTask')),
      Config: store.get('Config') || {}
    })

    function isCompleted (s = state.Selection) {
      const selection = s || state.Selection
      // console.log('isCompleted', state.Selection, state.Items?.length, Object.values(selection || {}).filter(Boolean).length >= state.Items?.length)
      return state.Items?.length && Object.values(selection || {}).filter(Boolean).length >= state.Items?.length
    }

    const completed = computed(isCompleted)
    const references = ref<Array<any>>([])

    function getCurrentItemIndex (id = state.Active): number {
      const idString = typeof id === 'object' ? $get(id, (state.Config.options?.selectionId || props.selectionId) || '') : id
      return state.Items.findIndex(i => typeof i === 'object' ? $get(i, (state.Config.options?.selectionId || props.selectionId) || '') === idString : i === idString)
    }

    // updpate selection
    function select (id, value = true) {
      if (props.hooks?.select) {
        if (typeof props.hooks.select === 'function') props.hooks.select(id)
        else console.warn('SelectableList:hooks.select | unable to call, select is not a function')
      }

      // update global store
      // console.log('select', props.parent, state.Active, id, value)

      void store.set(STORE_EVENTS_DICT.select, props.parent || state.Active, id, typeof value === 'boolean' ? value : true)
      emit('select', id)

      // NOTE instead of calling complete here directly, let the watcher handle it
      // if (completed.value) complete()

      if (props.nextOnSelect) void store.set('Next')
      if (props.nextItemOnSelect) {
        // NOTE this is to account for an object being passed to select / activeItem being an object (as is with usage of asTasks for example)
        const nextIndex = getCurrentItemIndex(id) + 1
        const isNext = state.Items[nextIndex]

        void store.set('ActiveItem', typeof isNext === 'object' ? $get(isNext, (state.Config.options?.selectionId || props.selectionId) || '') : isNext)
      }
    }

    function add (id) {
      if (props.hooks?.add) {
        if (typeof props.hooks.add === 'function') props.hooks.add(id)
        else console.warn('SelectableList:hooks.add | unable to call, add is not a function')
      }

      // update global store
      void store.set(STORE_EVENTS_DICT.add, id)
      emit('add', id)
    }

    // updpate selection
    function remove (id) {
      if (props.hooks?.remove) {
        if (typeof props.hooks.remove === 'function') props.hooks.remove(id)
        else console.warn('SelectableList:hooks.remove | unable to call, remove is not a function')
      }

      store.set(STORE_EVENTS_DICT.remove, id).then(updated => {
        if (!updated) state.Selection[id] = false
      }).catch(console.error)
      emit('remove', id)
    }

    function complete (clear?: boolean, delay: number | undefined = props.completeDelay) {
      if (props.hooks?.complete) {
        if (typeof props.hooks.complete === 'function') props.hooks.complete()
        else console.warn('SelectableList:hooks.complete | unable to call, complete is not a function')
      }

      if (props.parent || state.Active) {
        void store.set(STORE_EVENTS_DICT.complete, props.parent || state.Active, clear, delay)
        emit('complete', props.parent || props.id, undefined, state.Items)

        if (props.nextOnComplete && completed.value) {
          // respect delay
          setTimeout(() => {
            const nextIndex = getCurrentItemIndex() + 1

            if (props.autoscroll === true || props.autoscroll === 'complete' || props.autoscroll === 'activeComplete' || props.autoscroll === 'selectComplete') scrollToIndex(nextIndex)
            void store.set('Next', false)
          }, delay || 0)
        }
      }
    }

    // Trigger internal complete emit on external selection mutation
    watch(() => state.Selection, (value, prev) => {
      const increased = (Object.values(value || {}).filter(Boolean).length - Object.values(prev || {}).filter(Boolean).length) > 0

      if (increased && state.ActiveItem && (props.autoscroll === true || props.autoscroll === 'select' || props.autoscroll === 'activeSelect' || props.autoscroll === 'selectComplete')) {
        scrollToIndex(getCurrentItemIndex(state.ActiveItem))
      }
      if (completed.value) complete()
    })

    watch(() => state.Active, (value, prev) => {
      if (value && (props.autoscroll === true || props.autoscroll === 'active' || props.autoscroll === 'activeSelect' || props.autoscroll === 'activeComplete'))
        scrollToIndex(getCurrentItemIndex())
    })

    watch(() => state.ActiveItem, (value, prev) => {
      if (value && (props.autoscroll === true || props.autoscroll === 'active' || props.autoscroll === 'activeSelect' || props.autoscroll === 'activeComplete'))
        scrollToIndex(getCurrentItemIndex(state.ActiveItem))
    })

    function activate (id) {
      if (id === undefined || id === null) {
        console.warn('SelectableList:activate | missing id...', id)
        return
      }

      void store.set(STORE_EVENTS_DICT.active, id)
      emit('active', id)

      const nextIndex = getCurrentItemIndex() + 1

      if (props.autoscroll === true || props.autoscroll === 'active' || props.autoscroll === 'activeSelect' || props.autoscroll === 'activeComplete') scrollToIndex(nextIndex)
      // TODO: set a scroll to element on activate. find a method of automatically reading the children of the item slot

    }

    onMounted(() => {
      if (completed.value === true) complete()
    })

    const scrollToIndex = debounce((index: number) => {
      if (props.autoscroll === false) return
      if (index < 0) return
      // list.value.scrollTo(index, props.scrollAlign)
      if (list.value) {
        // keeps a list of fucking sizes because q-virual-scroll removes elements when they're out of view for optimization, making them undefined...
        listItemDOMElements.value.forEach((e,i) => {
          if (!e?.getBoundingClientRect) return
          listItemDOMElementSizes.value[i] = (props.horizontal
          ? Math.floor(e?.getBoundingClientRect().width || listItemDOMElementSizes.value[i] || 0) * 1.05
          : Math.floor(e?.getBoundingClientRect().height || listItemDOMElementSizes.value[i] || 0) * 1.05
        )
        })

        const scrollValue = (listItemDOMElementSizes.value || []).slice(0, index)
          .reduce((sum,v) => sum + v, 0)

        const element: HTMLElement = list.value.$el
        element.scrollTo({
          top: props.horizontal ? undefined : scrollValue,
          left: props.horizontal ? scrollValue : undefined,
          behavior: 'smooth'
        })
      }
    }, 200)

    return {
      list, // list ref
      internalStore,
      scrollToIndex,
      state,
      add,
      remove,
      select,
      complete,
      completed,
      activate,
      cache,
      updateCache,
      scrollable_area,
      computedOverflowHeight,
      listItemDOMElements
    }
  }
})

